/**
 * The inspector: what is true where the caret stands, and what can be changed about it.
 *
 * It is the answer to "I have selected something -- now what?". Five panels, each of which
 * is a different thing the selection can become: what is written on the bar, what stands
 * over it as a chord, what stands under it as figures, what is written over the notes, and
 * what the score itself is. The first line says where the caret is and how much is
 * selected, because every panel below acts on exactly that.
 *
 * The two typed marks are the panels the score can reach into: ⌘K over the music asks for
 * the caret to go to the chord box and ⌘G to the figures box, which means the panel has to
 * open. So their open state is held here rather than left to the browser, and the toggle is
 * read back so that a panel shut by hand can be opened again by the next press.
 *
 * Both boxes are the same component. A chord symbol and a stack of figures are written
 * differently and mean opposite things -- one names the harmony whatever the bass does, the
 * other names intervals over whatever bass there is -- but they are typed, corrected and
 * cleared in exactly the same motions, and one implementation of those motions is enough.
 */

import { useEffect, useState } from "react";
import {
  chordText,
  figures as figured,
  figuresText,
  harmony,
  measureIndexOf,
} from "@app/score-editor";
import type { ScoreActions } from "../lib/score-actions";
import ScoreInspectorBar from "./ScoreInspectorBar";
import ScoreInspectorMark from "./ScoreInspectorMark";
import ScoreInspectorText from "./ScoreInspectorText";
import ScoreSetup from "./ScoreSetup";
import { Kicker } from "./Page";
import type { ReactNode } from "react";

function Panel({
  title,
  children,
  open = false,
  onToggle,
}: {
  title: string;
  children: ReactNode;
  open?: boolean;
  onToggle?: (open: boolean) => void;
}): JSX.Element {
  return (
    <details
      open={open}
      onToggle={(event) => onToggle?.(event.currentTarget.open)}
      className="rounded-xl border border-rule bg-white px-3 py-2.5"
    >
      <summary className="cursor-pointer select-none font-mono text-[10px] uppercase tracking-[0.14em] text-ink-quiet">
        {title}
      </summary>
      <div className="mt-3">{children}</div>
    </details>
  );
}

export interface ScoreInspectorProps {
  readonly actions: ScoreActions;
  /** How many times the score has asked for a chord to be typed, rising by one each time. */
  readonly chordAsk: number;
  /** The same count for the figures box, which ⌘G raises. */
  readonly figureAsk: number;
}

export default function ScoreInspector({
  actions,
  chordAsk,
  figureAsk,
}: ScoreInspectorProps): JSX.Element {
  const { caret, edit, score, selection } = actions;
  const index = caret === null ? -1 : measureIndexOf(score, caret.measureId);
  const part =
    caret === null ? null : score.parts.find((candidate) => candidate.id === caret.partId);
  const selected = selection.events.size;
  const idle = selected === 0;

  const [chords, setChords] = useState(false);
  useEffect(() => {
    if (chordAsk > 0) setChords(true);
  }, [chordAsk]);

  const [figures, setFigures] = useState(false);
  useEffect(() => {
    if (figureAsk > 0) setFigures(true);
  }, [figureAsk]);

  const chord = harmony.at(score, selection);
  const stack = figured.at(score, selection);

  return (
    <aside className="space-y-2">
      <div className="space-y-1 rounded-xl border border-rule bg-white px-3 py-2.5">
        <Kicker>Where you are</Kicker>
        <p className="text-[13px] leading-relaxed text-ink">
          {caret === null ? "Nothing is selected." : `Bar ${index + 1} · ${part?.name ?? "part"}`}
        </p>
        <p className="font-mono text-[11px] text-ink-quiet">
          {idle
            ? "Click a note, or drag across a passage."
            : `${selected} ${selected === 1 ? "event" : "events"} selected`}
        </p>
      </div>

      <Panel title="Bar" open>
        <ScoreInspectorBar score={score} caret={caret} index={index} edit={edit} />
      </Panel>

      <Panel title="Chord symbol" open={chords} onToggle={setChords}>
        <ScoreInspectorMark
          label="Over the selected note"
          placeholder="Cmaj7"
          standing={chord === null ? "" : chordText(chord)}
          writeLabel="Write the chord"
          help="A root, then how the chord goes: m, 7, maj7, m7b5, sus4, 13. A slash bass after it, as in F/A. ⌘K comes here from the score."
          open={chords}
          askedAt={chordAsk}
          idle={idle}
          write={(text) => edit(harmony.set(score, selection, text), "chord symbol")}
          remove={() => edit(harmony.clear(score, selection), "remove the chord symbols")}
        />
      </Panel>

      <Panel title="Figured bass" open={figures} onToggle={setFigures}>
        <ScoreInspectorMark
          label="Under the selected note"
          placeholder="6/4"
          standing={stack === null ? "" : figuresText(stack)}
          writeLabel="Write the figure"
          help="The stack top down, split by /: 6, 6/4, #6/5. An accidental before a number alters it, one after it is the sign printed beside it, \ is the struck one, _ carries the line on, and (…) brackets the stack. Figures do not transpose, and the renderer draws them above the staff. ⌘G comes here from the score."
          open={figures}
          askedAt={figureAsk}
          idle={idle}
          write={(text) => edit(figured.set(score, selection, text), "figured bass")}
          remove={() => edit(figured.clear(score, selection), "remove the figured bass")}
        />
      </Panel>

      <Panel title="Words and pitch">
        <ScoreInspectorText score={score} selection={selection} edit={edit} />
      </Panel>

      <ScoreSetup score={score} at={caret?.measureId ?? null} edit={edit} />
    </aside>
  );
}
